// BUILDER BRIDGE — canal postMessage entre o site (rodando dentro de um <iframe>
// no painel do Builder do CRM) e o painel pai (app.imobtotal.com.br).
//
// Só ativa quando:
//   • o host é de teste/local (MESMA checagem client-safe usada em outros
//     pontos do site — utils/url.ts isLocalOrTestHost/normalizeHost são
//     funções puras de string, sem I/O — client-safe por construção); E
//   • a URL tem `?builder=1`.
// Em qualquer domínio de cliente real, `initBuilderBridge()` é NO-OP.
//
// ---------------------------------------------------------------------------
// PROTOCOLO (postMessage), payloads EXATOS:
//
// Site -> Builder, ao carregar (assim que o bridge ativa):
//   { action: 'builder-ready' }
//
// Builder -> Site:
//   { action: 'goto-section', section: string }
//     Localiza o 1º elemento com data-builder-section === section (se ainda não
//     montou — a home lazy-carrega o que está abaixo da dobra — rola em passos
//     até o elemento aparecer), dá
//     scrollIntoView({behavior:'smooth', block:'center'}) e aplica um outline
//     temporário (2s, classe injetada). Não encontrou -> responde:
//   Site -> Builder:
//     { action: 'section-not-found', section: string }
//
//   { action: 'picker', enabled: boolean, sections?: Record<string, string> }
//     Liga/desliga o modo de seleção de SEÇÃO. A seleção é SNAP-PRA-SEÇÃO: o
//     alvo do hover/clique é sempre o ancestral [data-builder-section] mais
//     próximo do ponto — clicou no botão da busca, realça/seleciona a seção
//     busca INTEIRA; elemento sem ancestral [data-builder-section] NÃO é
//     selecionável (nada realça, o clique não faz nada). O mapa opcional
//     `sections` (chave da seção -> rótulo amigável, ex.: {busca:'Busca'}) só
//     alimenta o tooltip ("Editar seção: Busca"); sem ele o tooltip mostra a
//     própria chave. ESC também desliga (avisa o builder com a mesma mensagem
//     'picker', enabled:false, via postMessage). Cliques em <a> (e qualquer
//     elemento) são bloqueados (preventDefault + stopPropagation) enquanto o
//     picker está ligado — nunca navega.
//
// Site -> Builder, no clique com o picker ligado (o alvo é sempre a raiz de
// uma seção catalogada):
//   {
//     action: 'element-picked',
//     selector: string,   // seletor ESTÁVEL, sempre posicional ANCORADO: âncora
//                         // estável (id/classe/[data-builder-section]) + cadeia
//                         // `> tag:nth-of-type(k)`. Nunca posicional PURO (sem
//                         // âncora) e nunca tag CRUA — exceto o caso patológico
//                         // de elemento sem âncora nenhuma até o body, em que o
//                         // backend recusa com mensagem amigável (ver
//                         // buildStableSelector).
//     tag: string,         // tagName em minúsculas
//     classes: string[],   // classList do elemento clicado
//     rect: { w: number; h: number },
//     text: string,        // primeiros 80 chars do textContent (trim)
//     html: string,        // outerHTML truncado a 2000 chars (teto que o backend
//                          // aplica em elementHtml — o alvo é a seção inteira,
//                          // então a IA precisa enxergar o miolo dela)
//   }
// ---------------------------------------------------------------------------
import { isLocalOrTestHost } from '../utils/url';

type GotoSectionMessage = { action: 'goto-section'; section: string };
type PickerMessage = { action: 'picker'; enabled: boolean; sections?: Record<string, string> };
type InboundMessage = GotoSectionMessage | PickerMessage;

type SectionNotFoundMessage = { action: 'section-not-found'; section: string };
type BuilderReadyMessage = { action: 'builder-ready' };
type ElementPickedMessage = {
  action: 'element-picked';
  selector: string;
  tag: string;
  classes: string[];
  rect: { w: number; h: number };
  text: string;
  html: string;
};
type OutboundMessage = SectionNotFoundMessage | BuilderReadyMessage | ElementPickedMessage | PickerMessage;

const PROD_BUILDER_ORIGIN = 'https://app.imobtotal.com.br';
const LOCALHOST_ORIGIN_PATTERN = /^http:\/\/localhost(:\d+)?$/i;

const HIGHLIGHT_CLASS = 'builder-bridge-highlight';
const HIGHLIGHT_DURATION_MS = 2000;
const PICKER_HOVER_CLASS = 'builder-bridge-picker-hover';
const TOOLTIP_ID = 'builder-bridge-tooltip';
const STYLE_TAG_ID = 'builder-bridge-styles';
// OVERLAY do picker: absorve TODOS os eventos de ponteiro enquanto o modo de
// seleção está ligado. Como ele fica por cima de tudo (z-index quase máximo,
// pointer-events auto), nenhum clique/hover chega ao documento real — nem
// <a target=_blank>, nem window.open disparado em handlers de mousedown/click
// dos componentes (ex.: FAB de WhatsApp). É a garantia POR CONSTRUÇÃO de que o
// picker nunca navega/dispara efeito colateral.
const OVERLAY_ID = 'builder-bridge-picker-overlay';
const HIGHLIGHT_BOX_ID = 'builder-bridge-picker-highlight-box';
// z-index: overlay logo abaixo do tooltip; a caixa de highlight abaixo do overlay
// (o overlay é transparente, então a caixa continua visível através dele).
const OVERLAY_Z = 2147483646;

// -----------------------------------------------------------------------
// Ativação (client-safe): mesma checagem de host de teste usada no resto do
// site (isLocalOrTestHost já é pura/client-safe — reusada em pages/index.tsx
// e pages/_app.tsx no client), + ?builder=1 na URL.
// -----------------------------------------------------------------------
export const isBuilderBridgeHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isLocalOrTestHost(window.location.host);
};

export const isBuilderQueryActive = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('builder') === '1';
  } catch {
    /* URL malformada — nunca deveria acontecer no browser real */
    return false;
  }
};

// Atalho SÓ de teste (host de teste/local): ?builderPicker=1 liga o modo de
// seleção direto, sem depender do postMessage do parent (que exige checagem de
// origin e um <iframe> real). Serve pra validação manual/automatizada (Playwright)
// do overlay. Em domínio de cliente real o bridge é NO-OP, então é inócuo.
export const isBuilderPickerQueryActive = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('builderPicker') === '1';
  } catch {
    /* URL malformada — nunca deveria acontecer no browser real */
    return false;
  }
};

export const shouldActivateBuilderBridge = (): boolean =>
  isBuilderBridgeHost() && (isBuilderQueryActive() || isBuilderPickerQueryActive());

const isAllowedOrigin = (origin: string): boolean =>
  origin === PROD_BUILDER_ORIGIN || LOCALHOST_ORIGIN_PATTERN.test(origin);

// -----------------------------------------------------------------------
// Estilos injetados uma única vez (outline de destaque + hover do picker +
// tooltip). Mantido isolado num <style> próprio pra não tocar em CSS Modules.
// -----------------------------------------------------------------------
let stylesInjected = false;
const ensureStylesInjected = (): void => {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .${HIGHLIGHT_CLASS} {
      outline: 3px solid #7c3aed !important;
      outline-offset: 2px;
      border-radius: 6px;
    }
    .${PICKER_HOVER_CLASS} {
      outline: 2px dashed #7c3aed !important;
      outline-offset: 2px;
    }
    html[data-builder-picker-active="1"], html[data-builder-picker-active="1"] * {
      cursor: crosshair !important;
    }
    #${TOOLTIP_ID} {
      position: fixed;
      z-index: 2147483647;
      pointer-events: none;
      background: #1f1f1f;
      color: #fff;
      font: 12px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      padding: 2px 8px;
      border-radius: 4px;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }
  `;
  document.head.appendChild(style);
};

// -----------------------------------------------------------------------
// helpers de seletor ESTÁVEL — nunca nth-child puro (posição na árvore muda
// facilmente com A/B de conteúdo, carrosséis, filtros etc.). Quando cai no
// posicional, é sempre ANCORADO numa âncora estável (id/classe/[data-builder-
// section]) + cadeia `> tag:nth-of-type(k)`, nunca posicional solto nem tag crua.
// -----------------------------------------------------------------------
const cssEscapeToken = (value: string): string => {
  if (typeof window !== 'undefined' && typeof (window as any).CSS?.escape === 'function') {
    try {
      return (window as any).CSS.escape(value);
    } catch {
      /* cai no fallback abaixo */
    }
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (char) => `\\${char}`);
};

// CSS Modules (Next.js) gera classes com sufixo hasheado tipo
// "Home_container__a1B2c" — essas não sobrevivem a um novo build (hash muda),
// então são descartadas como candidatas a seletor estável.
const looksLikeHashedClass = (cls: string): boolean => /__[a-zA-Z0-9]{4,8}$/.test(cls);

const isStableClassCandidate = (cls: string): boolean =>
  Boolean(cls) && !looksLikeHashedClass(cls);

// ids gerados em runtime (React useId, Radix, Material UI, etc.) costumam ter
// prefixos conhecidos ou blocos longos de hex/base36 — não são estáveis entre
// cargas de página.
const looksLikeGeneratedId = (id: string): boolean =>
  /^(mui-|radix-|react-|:r[0-9a-z]+:|headlessui-)/i.test(id) || /^[a-f0-9]{8,}$/i.test(id);

const mostSpecificStableClass = (el: Element): string | null => {
  const candidates = Array.from(el.classList).filter(isStableClassCandidate);
  if (!candidates.length) return null;
  // heurística de "mais específica": a classe mais longa costuma ser a mais
  // descritiva/menos genérica (ex.: "cardDestaque" > "card").
  return candidates.reduce((best, current) => (current.length > best.length ? current : best));
};

// Token posicional de um nó entre seus irmãos: só a tag quando é o ÚNICO filho
// daquela tag (já casa sem ambiguidade), senão `tag:nth-of-type(k)` (k = posição
// 1-based entre os irmãos da MESMA tag). nth-of-TYPE (e não nth-child) porque
// conta só irmãos do mesmo tipo — resiste a nós de texto/comentário e a irmãos
// de outras tags entrando/saindo.
const nthOfTypeToken = (node: Element): string => {
  const tag = node.tagName.toLowerCase();
  const parent = node.parentElement;
  if (!parent) return tag;
  const sameType = Array.from(parent.children).filter((child) => child.tagName === node.tagName);
  if (sameType.length <= 1) return tag; // filho único daquela tag → a tag basta
  return `${tag}:nth-of-type(${sameType.indexOf(node) + 1})`;
};

// Cadeia posicional com combinador FILHO ('>') do ancestral `anchor` (EXCLUSIVE)
// até `el` (inclusive), cada nível via nthOfTypeToken. Só é usada colada num
// seletor de âncora estável — nunca sozinha. Cap de profundidade: cadeia longa
// demais é frágil → retorna null (o chamador cai no fallback seguro e o backend
// recusa a tag crua). Também retorna null se `anchor` não é ancestral de `el`.
const POSITIONAL_CHAIN_MAX_DEPTH = 6;
const positionalChainFrom = (anchor: Element, el: Element): string | null => {
  const chain: string[] = [];
  let current: Element | null = el;
  while (current && current !== anchor) {
    chain.unshift(nthOfTypeToken(current));
    if (chain.length > POSITIONAL_CHAIN_MAX_DEPTH) return null; // estourou o cap
    current = current.parentElement;
  }
  if (current !== anchor) return null; // âncora não é ancestral de `el`
  return chain.join(' > ');
};

// Seletor de ÂNCORA estável de um elemento — [data-builder-section], id não-gerado
// ou classe estável mais específica (mesmos critérios dos ramos diretos) — ou null
// se o elemento não tem nenhuma âncora estável.
const stableAnchorSelector = (el: Element): string | null => {
  const section = el.getAttribute('data-builder-section');
  if (section !== null) return `[data-builder-section="${cssEscapeToken(section)}"]`;
  if (el.id && !looksLikeGeneratedId(el.id)) return `#${cssEscapeToken(el.id)}`;
  const specificClass = mostSpecificStableClass(el);
  if (specificClass) return `${el.tagName.toLowerCase()}.${cssEscapeToken(specificClass)}`;
  return null;
};

/**
 * Seletor ESTÁVEL do elemento clicado, nesta ordem de prioridade:
 *  1) ancestral [data-builder-section] + tag + classe mais específica não-hasheada;
 *  2) dentro da seção, id próprio não-gerado;
 *  3) dentro da seção, sem id/classe: posicional ANCORADO na seção
 *     (`[data-builder-section="x"] > div:nth-of-type(1) > img` — casa 1 nó);
 *  4) id próprio (se existir e não parecer gerado em runtime);
 *  5) cadeia curta de classes (tag + classes não-hasheadas do próprio elemento);
 *  6) sem seção nem id/classe próprios: sobe até um ancestral com âncora estável
 *     (id/classe/[data-builder-section]) e monta `âncora > cadeia posicional`.
 * NUNCA posicional PURO (sem âncora) e NUNCA tag crua — exceto o caso patológico
 * de não haver âncora nenhuma até o body (ou a cadeia estourar o cap): aí devolve
 * só a tag, que o backend (stripUnsafeHideRules) recusa com mensagem amigável.
 * Invariante: fora do caso patológico, o seletor sempre contém `.`/`#`/`[`.
 */
export const buildStableSelector = (el: Element): string => {
  const tag = el.tagName.toLowerCase();
  const sectionAncestor = el.closest('[data-builder-section]');

  if (sectionAncestor) {
    const sectionKey = sectionAncestor.getAttribute('data-builder-section') || '';
    const scope = `[data-builder-section="${cssEscapeToken(sectionKey)}"]`;
    const specificClass = mostSpecificStableClass(el);
    if (specificClass) return `${scope} ${tag}.${cssEscapeToken(specificClass)}`;
    if (el.id && !looksLikeGeneratedId(el.id)) return `${scope} #${cssEscapeToken(el.id)}`;
    // Fallback ANCORADO na seção: cadeia posicional com combinador filho da raiz
    // da seção até o elemento — casa EXATAMENTE 1 nó, nunca `[...] img` (que
    // esconderia a categoria inteira dentro da seção mesmo passando pela guarda).
    if (el === sectionAncestor) return scope; // clicou na própria raiz da seção
    const chain = positionalChainFrom(sectionAncestor, el);
    if (chain) return `${scope} > ${chain}`;
    // cadeia estourou o cap → tag crua (o backend recusa com mensagem amigável)
    return tag;
  }

  if (el.id && !looksLikeGeneratedId(el.id)) {
    return `#${cssEscapeToken(el.id)}`;
  }

  const stableClasses = Array.from(el.classList).filter(isStableClassCandidate);
  if (stableClasses.length > 0) {
    return `${tag}.${stableClasses.map(cssEscapeToken).join('.')}`;
  }

  // Sem seção, sem id/classe próprios: sobe procurando um ancestral com âncora
  // estável e monta `âncora > cadeia posicional` (o 1º ancestral com âncora é o
  // mais próximo → cadeia mais curta). Sem âncora nenhuma até o body → tag crua
  // (backend recusa).
  let ancestor: Element | null = el.parentElement;
  while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
    const anchor = stableAnchorSelector(ancestor);
    if (anchor) {
      const chain = positionalChainFrom(ancestor, el);
      if (chain) return `${anchor} > ${chain}`;
      break; // achou âncora, mas a cadeia estourou o cap → fallback seguro
    }
    ancestor = ancestor.parentElement;
  }

  return tag;
};

// -----------------------------------------------------------------------
// goto-section: busca por comparação exata de atributo (evita qualquer risco
// de "escape de seletor CSS" com valor vindo de postMessage).
// -----------------------------------------------------------------------
const findSectionElement = (section: string): HTMLElement | null => {
  const nodes = document.querySelectorAll<HTMLElement>('[data-builder-section]');
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getAttribute('data-builder-section') === section) return nodes[i];
  }
  return null;
};

// -----------------------------------------------------------------------
// bridge principal
// -----------------------------------------------------------------------
export const initBuilderBridge = (): (() => void) => {
  if (!shouldActivateBuilderBridge()) return () => {};

  ensureStylesInjected();

  let parentWindow: Window | null = null;
  let parentOrigin = '';
  let pickerEnabled = false;
  let tooltipEl: HTMLElement | null = null;
  let overlayEl: HTMLElement | null = null;
  let highlightBoxEl: HTMLElement | null = null;
  let highlightTimer: number | null = null;
  // Rótulos amigáveis das seções (chave -> rótulo), enviados pelo builder junto
  // com a mensagem 'picker'. Builder antigo não manda → mapa vazio → o tooltip
  // cai na própria chave da seção.
  let sectionLabels: Record<string, string> = {};

  const postToBuilder = (message: OutboundMessage) => {
    if (!parentWindow) return;
    try {
      parentWindow.postMessage(message, parentOrigin);
    } catch {
      /* parent fechou/navegou — sem destino, ignora */
    }
  };

  const clearHighlight = (el: HTMLElement) => {
    el.classList.remove(HIGHLIGHT_CLASS);
  };

  // A home monta as seções abaixo da dobra por LAZY-LOAD (PageSpeed): no topo da
  // página só topo/busca existem no DOM — rodapé, blog, equipe etc. só montam
  // quando a rolagem chega perto. Quando o alvo não existe ainda, CAÇA rolando
  // em passos de ~1 viewport até ele montar; se chegar ao fim sem achar (seção
  // desativada/sem conteúdo), devolve a rolagem e avisa o builder. Um novo
  // goto-section no meio da caça aborta a anterior (gotoSeq).
  let gotoSeq = 0;
  const gotoSection = async (section: string) => {
    const seq = ++gotoSeq;
    let target = findSectionElement(section);
    if (!target) {
      const startY = window.scrollY;
      const step = Math.max(window.innerHeight * 0.9, 400);
      for (let i = 0; i < 40 && !target && seq === gotoSeq; i += 1) {
        window.scrollBy(0, step);
        await new Promise((r) => setTimeout(r, 160));
        target = findSectionElement(section);
        const doc = document.scrollingElement;
        if (!target && doc && window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
          // fim da página: um último fôlego pro mount tardio
          await new Promise((r) => setTimeout(r, 400));
          target = findSectionElement(section);
          break;
        }
      }
      if (seq !== gotoSeq) return; // outro pedido assumiu a rolagem
      if (!target) {
        window.scrollTo({ top: startY });
        postToBuilder({ action: 'section-not-found', section });
        return;
      }
    }

    const found = target; // narrowing sobrevive ao closure do timer
    found.scrollIntoView({ behavior: 'smooth', block: 'center' });
    found.classList.add(HIGHLIGHT_CLASS);
    if (highlightTimer) window.clearTimeout(highlightTimer);
    highlightTimer = window.setTimeout(() => clearHighlight(found), HIGHLIGHT_DURATION_MS);
  };

  const ensureTooltip = (): HTMLElement => {
    if (tooltipEl) return tooltipEl;
    const el = document.createElement('div');
    el.id = TOOLTIP_ID;
    document.body.appendChild(el);
    tooltipEl = el;
    return el;
  };

  const removeTooltip = () => {
    tooltipEl?.remove();
    tooltipEl = null;
  };

  const positionTooltip = (target: HTMLElement, pointerX: number, pointerY: number) => {
    const tooltip = ensureTooltip();
    // O alvo é sempre a raiz de uma seção catalogada → mostra o nome que o
    // usuário vê na lista "Seções do site" (fallback: a chave crua).
    const key = target.getAttribute('data-builder-section') || '';
    const label = sectionLabels[key] || key;
    tooltip.textContent = `Editar seção: ${label}`;
    tooltip.style.display = 'block';
    tooltip.style.left = `${pointerX + 12}px`;
    tooltip.style.top = `${pointerY + 12}px`;
  };

  // -------------------------------------------------------------------------
  // OVERLAY do picker — absorve todos os eventos. O elemento sob o ponteiro é
  // resolvido com document.elementFromPoint IGNORANDO o overlay (desliga
  // pointer-events só durante a medição). A caixa de highlight e o tooltip são
  // pointer-events:none, então nunca são retornados por elementFromPoint.
  // -------------------------------------------------------------------------
  const isOwnNode = (node: Element | null): boolean =>
    node === overlayEl || node === highlightBoxEl || node === tooltipEl;

  const elementUnderPoint = (x: number, y: number): HTMLElement | null => {
    if (!overlayEl) return document.elementFromPoint(x, y) as HTMLElement | null;
    const previous = overlayEl.style.pointerEvents;
    overlayEl.style.pointerEvents = 'none';
    let el = document.elementFromPoint(x, y) as HTMLElement | null;
    // fallback defensivo: se ainda cair num nó nosso, pega o próximo da pilha.
    if (isOwnNode(el) && typeof document.elementsFromPoint === 'function') {
      const stack = document.elementsFromPoint(x, y) as HTMLElement[];
      el = (stack.find((candidate) => !isOwnNode(candidate)) as HTMLElement) || null;
    }
    overlayEl.style.pointerEvents = previous || 'auto';
    return el;
  };

  // O picker do Builder edita SEÇÕES catalogadas ("Seções do site"), não nós
  // soltos: o alvo é sempre o ancestral [data-builder-section] mais próximo do
  // ponto — clicou no botão da busca, seleciona a seção busca INTEIRA. Fora de
  // seção não há alvo (nada realça, clique não faz nada).
  const sectionUnderPoint = (x: number, y: number): HTMLElement | null => {
    const el = elementUnderPoint(x, y);
    if (!el) return null;
    return (el.closest('[data-builder-section]') as HTMLElement | null) ?? null;
  };

  const ensureHighlightBox = (): HTMLElement => {
    if (highlightBoxEl) return highlightBoxEl;
    const el = document.createElement('div');
    el.id = HIGHLIGHT_BOX_ID;
    el.style.cssText = [
      'position:fixed',
      'pointer-events:none',
      `z-index:${OVERLAY_Z - 1}`,
      'outline:2px solid #7c3aed',
      'outline-offset:0',
      'background:rgba(124,58,237,0.12)',
      'border-radius:4px',
      'display:none',
      'left:0',
      'top:0',
    ].join(';');
    document.body.appendChild(el);
    highlightBoxEl = el;
    return el;
  };

  const removeHighlightBox = () => {
    highlightBoxEl?.remove();
    highlightBoxEl = null;
  };

  const drawHighlight = (target: HTMLElement) => {
    const box = ensureHighlightBox();
    const rect = target.getBoundingClientRect();
    box.style.left = `${rect.left}px`;
    box.style.top = `${rect.top}px`;
    box.style.width = `${rect.width}px`;
    box.style.height = `${rect.height}px`;
    box.style.display = 'block';
  };

  const isPickableTarget = (el: HTMLElement | null): el is HTMLElement =>
    Boolean(el) && el !== document.documentElement && el !== document.body && !isOwnNode(el);

  const handleOverlayMove = (event: MouseEvent) => {
    if (!pickerEnabled) return;
    const target = sectionUnderPoint(event.clientX, event.clientY);
    if (!isPickableTarget(target)) {
      if (highlightBoxEl) highlightBoxEl.style.display = 'none';
      if (tooltipEl) tooltipEl.style.display = 'none';
      return;
    }
    drawHighlight(target);
    positionTooltip(target, event.clientX, event.clientY);
  };

  // O overlay bloqueia por construção; ainda assim absorvemos explicitamente os
  // eventos "iniciais" (mousedown/pointerdown/auxclick/contextmenu) porque alguns
  // componentes disparam window.open ANTES do click (mousedown/pointerdown) — e é
  // aí que popups escapariam se algo vazasse.
  const absorb = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOverlayClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!pickerEnabled) return;

    const target = sectionUnderPoint(event.clientX, event.clientY);
    if (!isPickableTarget(target)) return;

    const rect = target.getBoundingClientRect();
    const message: ElementPickedMessage = {
      action: 'element-picked',
      selector: buildStableSelector(target),
      tag: target.tagName.toLowerCase(),
      classes: Array.from(target.classList),
      rect: { w: Math.round(rect.width), h: Math.round(rect.height) },
      text: (target.textContent || '').trim().slice(0, 80),
      html: target.outerHTML.slice(0, 2000),
    };
    postToBuilder(message);
    // Observabilidade p/ teste (o bridge SÓ roda em host de teste/local, então
    // este evento local nunca dispara em domínio de cliente real).
    try {
      window.dispatchEvent(new CustomEvent('builder:element-picked', { detail: message }));
    } catch {
      /* CustomEvent indisponível — ambiente antigo; ignora */
    }
  };

  const ensureOverlay = (): HTMLElement => {
    if (overlayEl) return overlayEl;
    const el = document.createElement('div');
    el.id = OVERLAY_ID;
    el.style.cssText = [
      'position:fixed',
      'inset:0',
      `z-index:${OVERLAY_Z}`,
      'cursor:crosshair',
      'background:transparent',
    ].join(';');
    el.addEventListener('mousemove', handleOverlayMove);
    el.addEventListener('mousedown', absorb, true);
    el.addEventListener('pointerdown', absorb, true);
    el.addEventListener('auxclick', absorb, true);
    el.addEventListener('contextmenu', absorb, true);
    el.addEventListener('click', handleOverlayClick, true);
    document.body.appendChild(el);
    overlayEl = el;
    return el;
  };

  const removeOverlay = () => {
    overlayEl?.remove();
    overlayEl = null;
  };

  const setPickerEnabled = (enabled: boolean) => {
    pickerEnabled = enabled;
    document.documentElement.setAttribute('data-builder-picker-active', enabled ? '1' : '0');
    if (enabled) {
      ensureOverlay();
    } else {
      removeOverlay();
      removeHighlightBox();
      removeTooltip();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !pickerEnabled) return;
    setPickerEnabled(false);
    postToBuilder({ action: 'picker', enabled: false });
  };

  const handleMessage = (event: MessageEvent) => {
    if (!isAllowedOrigin(event.origin)) return;

    const data = event.data as InboundMessage | null | undefined;
    if (!data || typeof data !== 'object') return;

    // guarda a origem/janela do builder na 1ª mensagem válida recebida —
    // é pra ela que as respostas (section-not-found/element-picked) voltam.
    parentWindow = event.source as Window;
    parentOrigin = event.origin;

    if (data.action === 'goto-section' && typeof data.section === 'string') {
      void gotoSection(data.section);
      return;
    }

    if (data.action === 'picker' && typeof data.enabled === 'boolean') {
      // mapa chave->rótulo é opcional (builder antigo não manda) e só serve ao
      // tooltip — precisa estar guardado ANTES de ligar o picker.
      if (data.sections && typeof data.sections === 'object') {
        sectionLabels = data.sections as Record<string, string>;
      }
      setPickerEnabled(data.enabled);
    }
  };

  window.addEventListener('message', handleMessage);
  window.addEventListener('keydown', handleKeyDown, true);

  // Atalho de teste: ?builderPicker=1 liga o picker imediatamente (host de teste).
  if (isBuilderPickerQueryActive()) {
    setPickerEnabled(true);
  }

  // Anuncia pro builder assim que o bridge sobe. Nesse ponto ainda não recebemos
  // nenhuma mensagem do builder (não temos event.origin validado) — tenta inferir
  // a origem pelo document.referrer (setado pelo browser p/ o dono do <iframe>) e
  // só usa curinga ('*') se o referrer não bater com nenhuma origem permitida.
  // O handshake em si não carrega dado sensível (só o sinal "estou pronto").
  const resolveInitialParentOrigin = (): string => {
    try {
      if (document.referrer) {
        const referrerOrigin = new URL(document.referrer).origin;
        if (isAllowedOrigin(referrerOrigin)) return referrerOrigin;
      }
    } catch {
      /* referrer ausente/inválido — cai no curinga abaixo */
    }
    return '*';
  };

  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage(
        { action: 'builder-ready' } as BuilderReadyMessage,
        resolveInitialParentOrigin()
      );
    } catch {
      /* sem parent acessível — nada a fazer */
    }
  }

  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('keydown', handleKeyDown, true);
    if (highlightTimer) window.clearTimeout(highlightTimer);
    // desmonta overlay/highlight/tooltip e zera o estado do picker.
    setPickerEnabled(false);
    document.documentElement.removeAttribute('data-builder-picker-active');
  };
};
